import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  MapPin,
  Store,
  Box,
  ArrowLeft,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Car,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#00F0FF', '#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

export default function MallDetailPage() {
  const { mallId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [mall, setMall] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMallData();
  }, [mallId]);

  const fetchMallData = async () => {
    try {
      const [mallRes, shopsRes] = await Promise.all([
        api.get(`/malls/${mallId}`),
        api.get(`/shops?mall_id=${mallId}`),
      ]);
      setMall(mallRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Failed to fetch mall data:', error);
      toast.error('Failed to load mall details');
      navigate('/malls');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!mall) return null;

  // Calculate stats
  const occupiedShops = shops.filter((s) => s.status === 'occupied').length;
  const vacantShops = shops.filter((s) => s.status === 'vacant').length;
  const totalRent = shops.reduce((sum, s) => sum + (s.monthly_rent || 0), 0);
  const avgRent = shops.length ? totalRent / shops.length : 0;

  // Category breakdown
  const categoryData = shops.reduce((acc, shop) => {
    acc[shop.category] = (acc[shop.category] || 0) + 1;
    return acc;
  }, {});

  const pieData = Object.entries(categoryData).map(([name, value]) => ({
    name: name.replace('_', ' '),
    value,
  }));

  // Floor breakdown
  const floorData = shops.reduce((acc, shop) => {
    const floor = `Floor ${shop.floor}`;
    if (!acc[floor]) {
      acc[floor] = { floor, occupied: 0, vacant: 0 };
    }
    if (shop.status === 'occupied') {
      acc[floor].occupied += 1;
    } else {
      acc[floor].vacant += 1;
    }
    return acc;
  }, {});

  const barData = Object.values(floorData);

  return (
    <div className="space-y-6" data-testid="mall-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/malls')}
        className="mb-4"
        data-testid="back-to-malls"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Malls
      </Button>

      {/* Mall Header */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="relative h-64">
          <img
            src={mall.image_url || 'https://images.unsplash.com/photo-1766600115358-1c5ce9c7c87d?w=1200'}
            alt={mall.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <Badge className="mb-2 bg-primary/20 text-primary border-primary/30">
              {mall.group_name}
            </Badge>
            <h1 className="text-3xl font-bold text-white">{mall.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-white/80">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {mall.city}, {mall.state}
              </div>
              {mall.opening_year && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Est. {mall.opening_year}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{shops.length}</p>
                <p className="text-xs text-muted-foreground">Total Shops</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{mall.occupancy_rate?.toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">Occupancy</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">₹{(avgRent / 1000).toFixed(0)}K</p>
                <p className="text-xs text-muted-foreground">Avg Rent/mo</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Car className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{mall.parking_capacity?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Parking</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          className="btn-primary"
          onClick={() => navigate(`/digital-twin/${mallId}`)}
          data-testid="view-3d-twin"
        >
          <Box className="w-4 h-4 mr-2" />
          View 3D Twin
        </Button>
        <Button
          variant="outline"
          className="btn-secondary"
          onClick={() => navigate(`/inventory?mall=${mallId}`)}
          data-testid="view-inventory"
        >
          <Store className="w-4 h-4 mr-2" />
          View Inventory
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-muted/30">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="shops">Shops</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Floor Occupancy */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base">Floor-wise Occupancy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="floor" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="occupied" fill="#10B981" name="Occupied" />
                      <Bar dataKey="vacant" fill="#F59E0B" name="Vacant" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Mix */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base">Category Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {pieData.map((cat, i) => (
                    <div key={cat.name} className="flex items-center gap-2 text-sm">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[i % COLORS.length] }}
                      />
                      <span className="capitalize text-muted-foreground">{cat.name}</span>
                      <span className="font-mono ml-auto">{cat.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Mall Details */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-base">Mall Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium mt-1">{mall.address}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Area</p>
                  <p className="font-medium font-mono mt-1">{mall.total_area_sqft?.toLocaleString()} sq ft</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Floors</p>
                  <p className="font-medium font-mono mt-1">{mall.floors}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Opening Year</p>
                  <p className="font-medium font-mono mt-1">{mall.opening_year || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shops">
          <Card className="glass-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Floor</th>
                      <th>Brand</th>
                      <th>Category</th>
                      <th>Area</th>
                      <th>Rent/sqft</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shops.slice(0, 20).map((shop) => (
                      <tr key={shop.id}>
                        <td className="font-mono">{shop.unit_number}</td>
                        <td>Floor {shop.floor}</td>
                        <td>{shop.brand_name || '-'}</td>
                        <td className="capitalize">{shop.category.replace('_', ' ')}</td>
                        <td className="font-mono">{shop.carpet_area_sqft} sqft</td>
                        <td className="font-mono">₹{shop.rent_per_sqft}</td>
                        <td>
                          <Badge
                            className={`status-badge ${
                              shop.status === 'occupied'
                                ? 'status-occupied'
                                : shop.status === 'vacant'
                                ? 'status-vacant'
                                : 'status-reserved'
                            }`}
                          >
                            {shop.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-emerald-500">{occupiedShops}</p>
                <p className="text-sm text-muted-foreground mt-1">Occupied Shops</p>
                <Progress value={(occupiedShops / shops.length) * 100} className="h-2 mt-4" />
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-amber-500">{vacantShops}</p>
                <p className="text-sm text-muted-foreground mt-1">Vacant Shops</p>
                <Progress value={(vacantShops / shops.length) * 100} className="h-2 mt-4" />
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-primary">₹{(totalRent / 100000).toFixed(1)}L</p>
                <p className="text-sm text-muted-foreground mt-1">Monthly Revenue</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
